// 兑换表单 composable(Physical/Ticket 共用)
// UI-SPEC §4.1 §4.4 §4.7 完整状态机:填表 / 二次确认 / 成功 / 售空
// D-17 扩展:isSubmitting 幂等守门 + Toast.loading 遮盖(T-01-04-01)
// T-01-04-03:所有错误走 getBusinessErrorMessage / i18n 常量,不 render 原始 err.message

import { computed, ref } from 'vue-demi';
import type { Ref, ComputedRef } from 'vue-demi';
// Vue2 仓内惯例:require(...).use 避免 ESM interop(P03 index.vue 样板)
const { isApiBusinessError, isApiServerError } =
  require('@ark/web-api/frontend') as {
    isApiBusinessError: (err: unknown) => boolean;
    isApiServerError: (err: unknown) => boolean;
  };

type ToastApi = {
  loading: (options: {
    duration?: number;
    message?: string;
    zIndex?: number;
  }) => unknown;
  close: (token?: unknown) => void;
  error: (
    options: string | { message: string; duration?: number; zIndex?: number },
  ) => void;
};
const { useToast, useZIndex } = (
  require('@fegobull/webapi') as {
    use: { useToast: () => ToastApi; useZIndex: () => number };
  }
).use;

import { isValidName, isValidEmail } from '../utils/validators';
import { getBusinessErrorMessage } from '../utils/businessErrorMessage';
import t from '../utils/i18n';

/** 提交确认时库存并发耗尽：只 Toast 提示，不切换日历售空弹窗。 */
const OUT_OF_STOCK_BUSINESS_CODE = 31005;

/**
 * 提取兑换提交链路的业务错误码。
 *
 * 标准链路读 ApiError.businessCode；Fego request 对业务失败会把原始响应保留在
 * rawResponse.code，这里只补这个已验证形态，避免 shared 表单层承担过宽协议适配。
 */
function getBusinessCode(err: unknown): number | undefined {
  const source = err as {
    businessCode?: unknown;
    rawResponse?: { code?: unknown };
  };
  const bc = source.businessCode;
  if (typeof bc === 'number' && bc >= 30000) return bc;
  const rc = source.rawResponse?.code;
  if (typeof rc === 'number' && rc >= 30000) return rc;
  return undefined;
}

/**
 * 弹窗内状态机步骤(RedeemPhysicalModal / RedeemTicketModal 共享)
 * shared 包内定义避免跨包反向 import;user-prize-list 若需复用则 re-export。
 */
export type ModalStep =
  | 'event-selection' // 仅 Ticket:场次日历
  | 'wish-detail' // 仅 Ticket:wish 场次详情
  | 'form-input' // 填表(空/正在输入)
  | 'form-invalid' // 填表(inline 报错)
  | 'form-valid' // 填表(校验通过)
  | 'confirm' // 二次确认
  | 'success' // 兑换成功
  | 'sold-out'; // 门票售空 popup

export type SubmitFn = () => Promise<{ success: boolean }>;
export type RedeemFormField = 'name' | 'email';
type RedeemFormErrors = { name?: string; email?: string };
type RedeemFormTouched = Record<RedeemFormField, boolean>;

export type UseRedeemFormReturn = {
  name: Ref<string>;
  email: Ref<string>;
  errors: Ref<RedeemFormErrors>;
  step: Ref<ModalStep>;
  canSubmit: ComputedRef<boolean>;
  isSubmitting: Ref<boolean>;
  setFieldValue: (field: RedeemFormField, value: string) => void;
  validateField: (field: RedeemFormField) => boolean;
  validate: () => boolean;
  setStep: (s: ModalStep) => void;
  handleConfirm: () => Promise<void>;
  reset: () => void;
};

export function useRedeemForm(opts: { submit: SubmitFn }): UseRedeemFormReturn {
  const Toast = useToast();
  const name = ref('');
  const email = ref('');
  const errors = ref<RedeemFormErrors>({});
  const touched = ref<RedeemFormTouched>({ name: false, email: false });
  const step = ref<ModalStep>('form-input');
  const isSubmitting = ref(false);

  // 驱动 Confirm 按钮样式:未通过灰(disabled)vs 校验通过橙(enabled)
  const canSubmit = computed(
    () => isValidName(name.value) && isValidEmail(email.value),
  );

  function getFieldError(field: RedeemFormField): string | undefined {
    if (field === 'name') {
      return isValidName(name.value) ? undefined : t('Error.invalidName');
    }
    return isValidEmail(email.value) ? undefined : t('Error.invalidEmail');
  }

  function updateFillStep(nextErrors: RedeemFormErrors): void {
    if (Object.keys(nextErrors).length > 0) {
      step.value = 'form-invalid';
      return;
    }
    step.value = canSubmit.value ? 'form-valid' : 'form-input';
  }

  function validateField(field: RedeemFormField): boolean {
    touched.value = { ...touched.value, [field]: true };
    const error = getFieldError(field);
    const nextErrors: RedeemFormErrors = { ...errors.value };
    if (error) {
      nextErrors[field] = error;
    } else {
      delete nextErrors[field];
    }
    errors.value = nextErrors;
    updateFillStep(nextErrors);
    return !error;
  }

  function setFieldValue(field: RedeemFormField, value: string): void {
    if (field === 'name') {
      name.value = value;
    } else {
      email.value = value;
    }

    // 字段失焦后进入实时校验,避免用户修正后错误文案仍残留。
    if (touched.value[field] || errors.value[field]) {
      validateField(field);
      return;
    }
    updateFillStep(errors.value);
  }

  function validate(): boolean {
    touched.value = { name: true, email: true };
    const nextErrors: RedeemFormErrors = {};
    const nameError = getFieldError('name');
    const emailError = getFieldError('email');
    if (nameError) nextErrors.name = nameError;
    if (emailError) nextErrors.email = emailError;
    errors.value = nextErrors;
    const ok = Object.keys(nextErrors).length === 0;
    // 校验通过 → form-valid(可进二次确认),失败 → form-invalid(inline error)
    updateFillStep(nextErrors);
    return ok;
  }

  function setStep(s: ModalStep): void {
    step.value = s;
  }

  /**
   * 弹窗内错误提示统一提到 Modal 遮罩之上。
   */
  function showModalErrorToast(message: string, duration = 4000): void {
    Toast.error({
      message,
      duration,
      zIndex: useZIndex(),
    });
  }

  /**
   * 最终提交(二次确认 Confirm 点击后调用)
   * - 幂等守门:isSubmitting=true 期间直接 return(T-01-04-01)
   * - success → success(UI-SPEC §4.8,推翻 D-16 静默刷新)
   * - 业务错 31003 → sold-out 售空 popup(UI-SPEC §4.7)
   * - 业务错 31005 → Toast 提示并停留二确,处理提交瞬间库存竞争失败
   * - 其他业务错 → Toast.error 保留 confirm(用户可 Edit 回填)
   * - 网络错 → Toast.error 保留 confirm
   */
  async function handleConfirm(): Promise<void> {
    if (isSubmitting.value) return;
    isSubmitting.value = true;
    const loadingToken = Toast.loading({
      duration: 0,
      message: t('Modal.submitting'),
      zIndex: useZIndex(),
    });
    let loadingClosed = false;
    const closeLoading = (): void => {
      if (loadingClosed) return;
      Toast.close(loadingToken);
      loadingClosed = true;
    };
    try {
      await opts.submit();
      closeLoading();
      step.value = 'success';
    } catch (err) {
      const businessCode = getBusinessCode(err);
      if (businessCode !== undefined || isApiBusinessError(err)) {
        closeLoading();
        const resolvedBusinessCode =
          businessCode ?? (err as { businessCode: number }).businessCode;
        if (resolvedBusinessCode === OUT_OF_STOCK_BUSINESS_CODE) {
          showModalErrorToast(t('Error.ticketSoldOutDuringRedeem'), 4000);
          return;
        }
        const { message, popupKind } =
          getBusinessErrorMessage(resolvedBusinessCode);
        if (popupKind === 'sold-out') {
          step.value = 'sold-out';
        } else {
          // 停在二次确认,用户可点 Edit 回填;Toast 仅给提示
          showModalErrorToast(message);
        }
      } else if (isApiServerError(err)) {
        closeLoading();
        showModalErrorToast(t('Error.networkBusy'));
      } else {
        closeLoading();
        // 兜底:未知错不泄露 err.message(T-01-04-03)
        showModalErrorToast(t('Error.unknown'));
      }
    } finally {
      closeLoading();
      isSubmitting.value = false;
    }
  }

  function reset(): void {
    name.value = '';
    email.value = '';
    errors.value = {};
    touched.value = { name: false, email: false };
    step.value = 'form-input';
    isSubmitting.value = false;
  }

  return {
    name,
    email,
    errors,
    step,
    canSubmit,
    isSubmitting,
    setFieldValue,
    validateField,
    validate,
    setStep,
    handleConfirm,
    reset,
  };
}
